import React from 'react';
import {
  Alert,
  Box,
  CardMedia,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import noImage from '../assets/no-image.png';
import Button from '../components/Button';
import FreeGiftBadge from '../components/FreeGiftBadge';
import Modal from '../components/Modal';
import CheckoutPageHeader from '../components/checkout/CheckoutPageHeader';
import PageContainer from '../layouts/PageContainer';
import { v2Colors } from '../design/theme';
import { storageGet } from '../platform/storage';
import {
  V2_PAYMENT_TYPE,
  deliveryDayToYyyyMmDd,
  getDeliveryQuote,
  isCodPaymentType,
  isOnlinePaymentType,
} from '../services/checkout';
import { V2ApiError } from '../services/client';
import {
  clearCart,
  formatCartCustomizationSummary,
  getAppliedCouponDiscountRupee,
  getCart,
  computeCartCustomizationTotal,
  listCartCustomizationAddOns,
} from '../services/commerce';
import { formatAddressLine } from '../services/locationResolve';
import { buildPilotCodOrderPayload, createPilotOrder } from '../services/orders';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';

const LOCATION_KEY = 'v2_location';

const summaryCardSx = {
  bgcolor: v2Colors.surface,
  borderRadius: 3,
  p: { xs: 1.75, sm: 2 },
  mb: 2,
  boxShadow: 'none',
};

const sectionLabelSx = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: v2Colors.canvas,
  mb: 0.75,
};

function resolveDeliveryCoords(blob) {
  if (!blob) return { lat: NaN, lng: NaN };
  const lat = Number(blob.lat ?? blob.customerAddress?.latLong?.lat);
  const lng = Number(blob.lng ?? blob.customerAddress?.latLong?.lng);
  return { lat, lng };
}

function formatArrivalDay(deliveryDate) {
  if (!deliveryDate) return '';
  const today = deliveryDayToYyyyMmDd('today');
  const tomorrow = deliveryDayToYyyyMmDd('tomorrow');
  if (deliveryDate === today) return 'Today';
  if (deliveryDate === tomorrow) return 'Tomorrow';
  try {
    const d = new Date(`${deliveryDate}T12:00:00`);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
  } catch {
    return deliveryDate;
  }
}

function paymentSummaryLabel(paymentType) {
  const t = paymentType ?? V2_PAYMENT_TYPE.COD;
  if (isCodPaymentType(t)) return 'Cash on delivery';
  if (isOnlinePaymentType(t)) return 'Online payment · UPI';
  return String(t);
}

function BillRow({ label, value, bold }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 2,
        mb: bold ? 0 : 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: v2Colors.heading,
          fontWeight: bold ? 800 : 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant={bold ? 'subtitle1' : 'body2'}
        sx={{
          fontWeight: bold ? 800 : 700,
          color: v2Colors.heading,
          flexShrink: 0,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SummaryArrivalCard({ deliveryDate, timeSlot, storeName, distanceKm }) {
  const day = formatArrivalDay(deliveryDate);
  const slotLabel = timeSlot?.displayTime || String(timeSlot?.id ?? '');
  const storeLine = [
    storeName ? `From ${storeName} store` : null,
    distanceKm != null ? `${Number(distanceKm).toFixed(1)} km away` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box
      sx={{
        ...summaryCardSx,
        bgcolor: v2Colors.success,
        color: v2Colors.white,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          right: -24,
          top: -24,
          width: 100,
          height: 100,
          borderRadius: '50%',
          bgcolor: 'rgba(255,255,255,0.08)',
        }}
      />
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', position: 'relative' }}>
        <ScheduleRounded sx={{ fontSize: 28, mt: 0.25, opacity: 0.95 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.68rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              opacity: 0.9,
              mb: 0.5,
            }}
          >
            Arriving
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.35rem', sm: '1.5rem' },
              lineHeight: 1.2,
              mb: storeLine ? 0.5 : 0,
            }}
          >
            {day}
            {slotLabel ? ` · ${slotLabel}` : ''}
          </Typography>
          {storeLine ? (
            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
              {storeLine}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function SummaryCartLine({ line }) {
  const qty = Number(line.quantity) || 0;
  const unit = Number(line.price) || 0;
  const rowPrice = unit * qty;
  const addOns = listCartCustomizationAddOns(line.customizations);
  const variantLabel = line.variant?.weight ? String(line.variant.weight) : null;
  const detailParts = [`Qty ${qty}`, variantLabel].filter(Boolean);

  const tagLabels =
    addOns.length > 0
      ? addOns.map((a) => a.name)
      : formatCartCustomizationSummary(line.customizations)
        ? formatCartCustomizationSummary(line.customizations).split(' · ')
        : [];

  return (
    <Box sx={summaryCardSx}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <CardMedia
          component="img"
          image={line.imageUrl || noImage}
          alt={line.name || 'Product'}
          sx={{
            width: 72,
            height: 72,
            flexShrink: 0,
            borderRadius: 2,
            objectFit: 'cover',
            bgcolor: v2Colors.surface,
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = noImage;
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.05rem',
                lineHeight: 1.25,
                color: v2Colors.heading,
                minWidth: 0,
              }}
            >
              {line.name}
              {line.isFreeSkuItem ? <FreeGiftBadge /> : null}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '1.05rem',
                color: v2Colors.heading,
                flexShrink: 0,
              }}
            >
              ₹{rowPrice}
            </Typography>
          </Box>
          {detailParts.length ? (
            <Typography variant="body2" sx={{ color: v2Colors.textMuted, fontSize: '0.8rem', mb: tagLabels.length ? 1 : 0 }}>
              {detailParts.join(' · ')}
            </Typography>
          ) : null}
          {tagLabels.length ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {tagLabels.map((tag) => (
                <Box
                  key={`${line.productId}-${tag}`}
                  sx={{
                    px: 1,
                    py: 0.35,
                    borderRadius: 1,
                    bgcolor: v2Colors.tagFill,
                    color: v2Colors.tagText,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                  }}
                >
                  {tag}
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

function SummaryDeliveryPaymentCard({ addr, mobile, paymentType, instructions }) {
  const addressLine = formatAddressLine(addr);

  return (
    <Box
      sx={{
        ...summaryCardSx,
        bgcolor: v2Colors.primary,
        color: v2Colors.canvas,
      }}
    >
      <Typography sx={sectionLabelSx}>Deliver to</Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '1rem',
          color: v2Colors.canvas,
          mb: 0.5,
        }}
      >
        {addr?.userName || '—'}
      </Typography>
      {addressLine ? (
        <Typography
          variant="body2"
          sx={{ color: v2Colors.canvas, lineHeight: 1.45, mb: mobile ? 0.5 : 0, opacity: 0.95 }}
        >
          {addressLine}
        </Typography>
      ) : null}
      {mobile ? (
        <Typography variant="body2" sx={{ color: v2Colors.canvas, opacity: 0.95 }}>
          +91 {mobile}
        </Typography>
      ) : null}

      {instructions ? (
        <>
          <Divider sx={{ my: 2, borderColor: 'rgba(248, 232, 204, 0.35)' }} />
          <Typography sx={sectionLabelSx}>Instructions</Typography>
          <Typography variant="body2" sx={{ color: v2Colors.canvas, whiteSpace: 'pre-wrap', opacity: 0.95 }}>
            {instructions}
          </Typography>
        </>
      ) : null}

      <Divider sx={{ my: 2, borderColor: 'rgba(248, 232, 204, 0.35)' }} />

      <Typography sx={sectionLabelSx}>Payment</Typography>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '1rem',
          color: v2Colors.canvas,
        }}
      >
        {paymentSummaryLabel(paymentType)}
      </Typography>
    </Box>
  );
}

export default function CheckoutSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;
  const { mobile, loggedIn } = useV2Session();

  const [loc, setLoc] = React.useState(null);
  const [cart, setCart] = React.useState({
    products: [],
    totalAmount: 0,
    totalCount: 0,
    coupon: null,
    instructions: '',
  });
  const [cartLoading, setCartLoading] = React.useState(true);
  const [placing, setPlacing] = React.useState(false);
  const [placeError, setPlaceError] = React.useState('');
  const [payFailureModalOpen, setPayFailureModalOpen] = React.useState(false);
  const [delivery, setDelivery] = React.useState({
    loading: false,
    error: '',
    charge: null,
    freeApplied: false,
    freeAbove: null,
    distanceKm: null,
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const blob = await storageGet(LOCATION_KEY);
      if (!cancelled) setLoc(blob);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadCart = React.useCallback(async () => {
    setCartLoading(true);
    try {
      const next = await getCart();
      setCart({
        products: Array.isArray(next.products) ? next.products : [],
        totalAmount: Number(next.totalAmount) || 0,
        totalCount: Number(next.totalCount) || 0,
        coupon: next.coupon && next.coupon.couponCode ? next.coupon : null,
        instructions: typeof next.instructions === 'string' ? next.instructions : '',
      });
    } finally {
      setCartLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCart();
  }, [loadCart]);

  React.useEffect(() => {
    const onCartChanged = () => {
      loadCart();
    };
    window.addEventListener('v2-cart-changed', onCartChanged);
    return () => window.removeEventListener('v2-cart-changed', onCartChanged);
  }, [loadCart]);

  React.useEffect(() => {
    if (state?.paymentFailed) {
      setPayFailureModalOpen(true);
    }
  }, [state?.paymentFailed]);

  const dismissPayFailureModal = React.useCallback(() => {
    setPayFailureModalOpen(false);
    const next = state && typeof state === 'object' ? { ...state } : {};
    delete next.paymentFailed;
    delete next.paymentFailureMessage;
    navigate(paths.checkoutSummary, { replace: true, state: next });
  }, [navigate, state]);

  const hasItems = cart.products.length > 0 && cart.totalCount > 0;

  const storeBranchId = loc?.store?.branchId ?? loc?.store?.branch_id;

  React.useEffect(() => {
    if (cartLoading || !hasItems || !storeBranchId) {
      setDelivery({
        loading: false,
        error: '',
        charge: null,
        freeApplied: false,
        freeAbove: null,
        distanceKm: null,
      });
      return undefined;
    }

    const { lat, lng } = resolveDeliveryCoords(loc);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setDelivery({
        loading: false,
        error: 'Add a delivery address with a map pin to see delivery fee.',
        charge: null,
        freeApplied: false,
        freeAbove: null,
        distanceKm: null,
      });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setDelivery((d) => ({ ...d, loading: true, error: '' }));
      try {
        const data = await getDeliveryQuote({
          storeBranchId,
          customerLat: lat,
          customerLng: lng,
          cartTotal: cart.totalAmount,
          deliveryType: 'delivery',
        });
        if (cancelled) return;
        setDelivery({
          loading: false,
          error: '',
          charge: Number(data.deliveryCharge) || 0,
          freeApplied: Boolean(data.freeDeliveryApplied),
          freeAbove: data.freeDeliveryAbove ?? null,
          distanceKm: data.distanceKm ?? null,
        });
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof V2ApiError ? e.message : 'Could not load delivery fee.';
        setDelivery({
          loading: false,
          error: msg,
          charge: null,
          freeApplied: false,
          freeAbove: null,
          distanceKm: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cartLoading, loc, storeBranchId, cart.totalAmount, hasItems]);

  const handlePlaceOrder = React.useCallback(async () => {
    setPlaceError('');
    if (!loggedIn || !mobile) {
      setPlaceError('Please sign in to place an order.');
      return;
    }
    if (!state?.deliveryDate || !state?.timeSlot?.id) return;
    if (!isCodPaymentType(state.paymentType)) {
      setPlaceError('Use Pay securely for online payment, or choose cash on delivery on checkout.');
      return;
    }
    const sub = Number(cart.totalAmount) || 0;
    const couponDisc = getAppliedCouponDiscountRupee(cart);
    const shipOk = delivery.charge != null && !delivery.error && !delivery.loading;
    const ship = shipOk ? Number(delivery.charge) || 0 : NaN;
    const totalAfter = sub + ship - couponDisc;
    if (!shipOk || !(totalAfter > 0)) {
      setPlaceError('Wait for delivery fee or fix cart before placing order.');
      return;
    }
    if (!loc?.storeId || !loc?.customerAddress) {
      setPlaceError('Missing store or address. Go back to checkout.');
      return;
    }

    setPlacing(true);
    try {
      const body = buildPilotCodOrderPayload({
        mobile,
        loc,
        checkoutState: state,
        cart,
        shippingCost: ship,
      });
      const res = await createPilotOrder(body);
      const orderId = res?.order?.orderId ?? res?.order?.id;
      const orderIdStr = orderId ? String(orderId) : '';

      const receiptProducts = (cart.products || []).map((line) => ({
        productId: line.productId,
        name: line.name,
        quantity: Number(line.quantity) || 0,
        price: Number(line.price) || 0,
        variant: line.variant ? { weight: line.variant.weight } : null,
      }));

      const receipt = {
        products: receiptProducts,
        subtotal: sub,
        shippingCost: ship,
        discount: couponDisc,
        appliedCoupon: cart.coupon?.couponCode ?? null,
        total: totalAfter,
        deliveryDate: state.deliveryDate,
        timeSlotLabel: state.timeSlot?.displayTime || String(state.timeSlot?.id ?? ''),
        paymentLabel: paymentSummaryLabel(state.paymentType),
      };

      await clearCart();
      navigate(paths.orderStatus, {
        replace: true,
        state: orderIdStr ? { orderId: orderIdStr, receipt } : {},
      });
    } catch (e) {
      setPlaceError(e instanceof V2ApiError ? e.message : 'Could not place order.');
    } finally {
      setPlacing(false);
    }
  }, [loggedIn, mobile, state, cart, delivery, loc, navigate]);

  const handlePayOnline = React.useCallback(() => {
    setPlaceError('');
    if (!loggedIn || !mobile) {
      setPlaceError('Please sign in to continue.');
      return;
    }
    if (!state?.deliveryDate || !state?.timeSlot?.id) return;
    if (!isOnlinePaymentType(state.paymentType)) return;

    const sub = Number(cart.totalAmount) || 0;
    const couponDisc = getAppliedCouponDiscountRupee(cart);
    const shipOk = delivery.charge != null && !delivery.error && !delivery.loading;
    const ship = shipOk ? Number(delivery.charge) || 0 : NaN;
    const totalAfter = sub + ship - couponDisc;
    if (!shipOk || !(totalAfter > 0)) {
      setPlaceError('Wait for delivery fee or fix cart before paying.');
      return;
    }
    if (!loc?.storeId || !loc?.customerAddress) {
      setPlaceError('Missing store or address. Go back to checkout.');
      return;
    }

    const body = buildPilotCodOrderPayload({
      mobile,
      loc,
      checkoutState: state,
      cart,
      shippingCost: ship,
    });

    const receiptProducts = (cart.products || []).map((line) => ({
      productId: line.productId,
      name: line.name,
      quantity: Number(line.quantity) || 0,
      price: Number(line.price) || 0,
      variant: line.variant ? { weight: line.variant.weight } : null,
    }));

    const receipt = {
      products: receiptProducts,
      subtotal: sub,
      shippingCost: ship,
      discount: couponDisc,
      appliedCoupon: cart.coupon?.couponCode ?? null,
      total: totalAfter,
      deliveryDate: state.deliveryDate,
      timeSlotLabel: state.timeSlot?.displayTime || String(state.timeSlot?.id ?? ''),
      paymentLabel: paymentSummaryLabel(state.paymentType),
    };

    navigate(paths.checkoutPay, {
      state: {
        pilotOrderPayload: body,
        receipt,
        checkoutSummaryState: {
          deliveryDate: state.deliveryDate,
          timeSlot: state.timeSlot,
          paymentType: state.paymentType,
          instructions: String(cart.instructions ?? state?.instructions ?? '').trim(),
        },
      },
    });
  }, [loggedIn, mobile, state, cart, delivery, loc, navigate]);

  if (!state?.deliveryDate || !state?.timeSlot) {
    return <Navigate to={paths.checkout} replace />;
  }

  const addr = loc?.customerAddress;
  const summaryInstructions = String(cart.instructions ?? state?.instructions ?? '').trim();
  const storeName = loc?.storeName ?? loc?.store?.storeName ?? null;

  const subtotal = Number(cart.totalAmount) || 0;
  const couponDiscount = getAppliedCouponDiscountRupee(cart);
  const customizationTotal = computeCartCustomizationTotal(cart.products);
  const itemTotal = Math.max(0, subtotal - customizationTotal);
  const deliveryReady = hasItems && !delivery.loading && !delivery.error && delivery.charge != null;
  const grandTotal =
    deliveryReady ? Math.max(0, subtotal + delivery.charge - couponDiscount) : null;

  const codSelected = isCodPaymentType(state.paymentType);
  const onlineSelected = isOnlinePaymentType(state.paymentType);
  const canPlaceOrder =
    loggedIn &&
    Boolean(mobile) &&
    hasItems &&
    grandTotal != null &&
    grandTotal > 0 &&
    codSelected &&
    Boolean(loc?.storeId) &&
    Boolean(addr) &&
    !placing &&
    !cartLoading;

  const canStartOnlinePay =
    loggedIn &&
    Boolean(mobile) &&
    hasItems &&
    grandTotal != null &&
    grandTotal > 0 &&
    onlineSelected &&
    Boolean(loc?.storeId) &&
    Boolean(addr) &&
    !cartLoading;

  const deliveryLabel =
    delivery.distanceKm != null
      ? `Delivery · ${Number(delivery.distanceKm).toFixed(1)} km`
      : 'Delivery';

  const ctaDisabled = codSelected ? !canPlaceOrder : !canStartOnlinePay;
  const ctaLoading = codSelected && placing;

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <CheckoutPageHeader backTo={paths.checkout} title="Confirm order" />

      {cartLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} sx={{ color: v2Colors.primary }} />
        </Box>
      ) : !hasItems ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
          Your bag is empty. Add items from the cart before placing an order.
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" size="small" onClick={() => navigate(paths.cart)} sx={{ borderRadius: 2 }}>
              Go to cart
            </Button>
          </Box>
        </Alert>
      ) : (
        <>
          <SummaryArrivalCard
            deliveryDate={state.deliveryDate}
            timeSlot={state.timeSlot}
            storeName={storeName}
            distanceKm={delivery.distanceKm}
          />

          {cart.products.map((line, index) => (
            <SummaryCartLine
              key={`${line.productId}-${line.isFreeSkuItem ? 'free' : 'line'}-${index}`}
              line={line}
            />
          ))}

          {addr ? (
            <SummaryDeliveryPaymentCard
              addr={addr}
              mobile={mobile}
              paymentType={state.paymentType}
              instructions={summaryInstructions}
            />
          ) : (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Address is read from saved location in app storage.
            </Alert>
          )}

          <Box sx={summaryCardSx}>
            <BillRow label="Item total" value={`₹${itemTotal}`} />
            {customizationTotal > 0 ? (
              <BillRow label="Customization" value={`₹${customizationTotal}`} />
            ) : null}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ color: v2Colors.heading, fontWeight: 600 }}>
                {deliveryLabel}
              </Typography>
              <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                {delivery.loading ? (
                  <CircularProgress size={20} sx={{ color: v2Colors.primary }} />
                ) : delivery.error ? (
                  <Typography variant="caption" color="error">
                    —
                  </Typography>
                ) : delivery.freeApplied ? (
                  <Typography variant="body2" fontWeight={600} sx={{ color: v2Colors.heading }}>
                    Free
                  </Typography>
                ) : delivery.charge != null ? (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: v2Colors.heading }}
                  >
                    ₹{delivery.charge}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
            </Box>
            {couponDiscount > 0 && cart.coupon?.couponCode ? (
              <BillRow label={`Coupon (${cart.coupon.couponCode})`} value={`−₹${couponDiscount}`} />
            ) : null}
            {delivery.error ? (
              <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', mb: 1 }}>
                {delivery.error}
              </Typography>
            ) : null}
            <Divider sx={{ my: 1.5, borderColor: v2Colors.border }} />
            <BillRow
              label="You pay"
              value={grandTotal != null ? `₹${grandTotal}` : '—'}
              bold
            />
          </Box>
        </>
      )}

      {placeError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {placeError}
        </Alert>
      ) : null}

      {hasItems && !cartLoading ? (
        <Box sx={{ mt: 1 }}>
          <Button
            fullWidth
            size="large"
            disabled={ctaDisabled || ctaLoading}
            onClick={codSelected ? handlePlaceOrder : handlePayOnline}
            sx={{
              borderRadius: 2,
              py: 1.5,
              bgcolor: v2Colors.primary,
              '&:hover': { bgcolor: v2Colors.primaryDark },
              '&.Mui-disabled': { bgcolor: v2Colors.border, color: v2Colors.white },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                fontWeight: 700,
                fontSize: '1rem',
              }}
            >
              <span>{codSelected ? (ctaLoading ? 'Placing order…' : 'Place order securely') : 'Pay securely'}</span>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {grandTotal != null ? `₹${grandTotal}` : ''}
                {!ctaLoading ? <ArrowForwardRounded sx={{ fontSize: 20 }} /> : null}
              </Box>
            </Box>
          </Button>

          {!loggedIn || !mobile ? (
            <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', textAlign: 'center', mt: 1 }}>
              Sign in to place an order.
            </Typography>
          ) : grandTotal == null ? (
            <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', textAlign: 'center', mt: 1 }}>
              Confirm delivery fee loads above before continuing.
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', textAlign: 'center', mt: 1.25 }}>
              By placing order you agree to our terms
            </Typography>
          )}
        </Box>
      ) : null}

      <Modal open={payFailureModalOpen} onClose={dismissPayFailureModal} title="Payment did not go through">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {typeof state?.paymentFailureMessage === 'string' && state.paymentFailureMessage.trim()
            ? state.paymentFailureMessage
            : 'Your payment could not be completed. You can try again or choose cash on delivery in checkout.'}
        </Typography>
        <Button fullWidth variant="contained" onClick={dismissPayFailureModal} sx={{ borderRadius: 2 }}>
          OK
        </Button>
      </Modal>
    </PageContainer>
  );
}
